import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  TailBiting,
  TailBitingModuleState,
  UpsertTailBitingResponse,
} from '../../model/tail-biting.model';
import { TailBitingService } from '../../services/tail-biting.service';

import * as actions from '../actions/tail-biting.actions';

@Injectable()
export class TailBitingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly store: Store<TailBitingModuleState>,
    private readonly tailBitingService: TailBitingService,
    private readonly dialogService: DialogService,
    private readonly localeService: LocaleService,
  ) {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadData),
      exhaustMap(() => of(actions.clearTailBitingState())),
    ),
  );

  getTailBitings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getTailBitingsRequest),
      exhaustMap((_) =>
        this.tailBitingService.getTailBitings().pipe(
          switchMap((tailBitings: TailBiting[]) =>
            of(actions.getTailBitingsSuccess({ tailBitings })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.getTailBitingsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setSelectedTailBiting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedTailBitingRequest),
      exhaustMap(({ id }) =>
        this.tailBitingService.getTailBitingById(id).pipe(
          switchMap((tailBiting: TailBiting) =>
            of(actions.setSelectedTailBitingSuccess({ tailBiting })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.setSelectedTailBitingError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  upsertTailBiting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.upsertTailBitingRequest),
      mergeMap((action) =>
        this.tailBitingService.upsertTailBiting(action.request).pipe(
          switchMap((response: UpsertTailBitingResponse) =>
            of(
              actions.upsertTailBitingSuccess({ request: action.request, response }),
              actions.getTailBitingsRequest(),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('tail-biting-saved'),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.upsertTailBitingError({ error }),
              actions.clearTailBitingState(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertTailBitingSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.upsertTailBitingSuccess),
        tap((action) => {
          if (
            !action.response.isOffline &&
            action.request.id === undefined &&
            action.response.id > 0
          ) {
            this.store.dispatch(actions.setSelectedTailBitingRequest({ id: action.response.id }));
          } else {
            this.store.dispatch(actions.clearSelectedTailBiting());
          }
        }),
      ),
    { dispatch: false },
  );

  deleteTailBitingWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteTailBitingWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('tail-biting.overview.delete-confirmation.title'),
            text: this.localeService.translate('tail-biting.overview.delete-confirmation.message'),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  actions.deleteTailBitingRequest({
                    tailBiting: action.tailBiting,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteTailBitingRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteTailBitingRequest),
      mergeMap((action) =>
        this.tailBitingService.deleteTailBiting(action.tailBiting.id).pipe(
          map(() => actions.deleteTailBitingSuccess({ tailBitingId: action.tailBiting.id })),
          catchError((_: HttpErrorResponse) =>
            of(
              NotificationActions.errorSnackbar({
                message: this.localeService.translate('tail-biting.overview.delete-error-message'),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteTailBitingSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.deleteTailBitingSuccess),
        tap((_) => this.router.navigate(['/tail-biting'])),
      ),
    { dispatch: false },
  );

  navigateCreateTailBiting$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.navigateCreateTailBiting),
        tap((action) => this.router.navigate(['/tail-biting/create', action.locationId])),
      ),
    {
      dispatch: false,
    },
  );
}
